import { supabase } from './supabase'

/**
 * Reversements des marchands — le SOIR MÊME de la livraison (migration 0065).
 *
 * Règle (décision du fondateur du 14/10/2026) : toutes les commandes livrées dans la journée (jusqu'à 23 h 59, heure de
 * Bamako) sont regroupées en UN reversement par marchand ; le dispatch fait les virements Orange Money / Wave à la main,
 * puis clique « Marquer tout comme versé ». Une retenue (remboursement, litige) décidée APRÈS un reversement est déduite du
 * suivant et reportée si elle le dépasse. Les frais de virement sont inclus dans notre commission.
 *
 * ⚠️ Rien ici ne calcule un montant : le serveur le fait (RPC), et refuse de marquer « versé » un montant qui a changé
 * depuis l'affichage. Le rejouer en TypeScript donnerait une seconde vérité sur ce qu'on doit à des gens.
 * ⚠️ Marquer « versé » n'EXÉCUTE aucun virement (aucune API Wave / Orange branchée) : cela enregistre qu'il a été fait à la main.
 */

export type ModeLot = 'historique' | 'quotidien' | 'par_commande'

export interface LotReversement {
  id: string
  marchandId: string
  marchandNom: string
  /** Jour du reversement (`period_end`, date de Bamako). */
  jour: string
  mode: ModeLot
  nbCommandes: number
  /** Ventes aux clients, articles seulement (jamais la livraison, le service, le pourboire). */
  ventes: number
  /** Commission SwiftEats TTC. */
  commission: number
  /** Retenues (négatif) et crédits (positif) absorbés dans ce lot. */
  retenues: number
  /** Dette des lots précédents déduite de ce lot. */
  reportEntrant: number
  /** Net à virer (jamais négatif). */
  net: number
  /** Dette restante après ce lot (le lot vaut alors 0). */
  reportSortant: number
  /** Frais de virement de ce versement (forfait de la config). */
  fraisVirement: number
  /** Part des frais retenue au marchand (0 : ils sont inclus dans notre commission). */
  fraisMarchand: number
  statut: string
  payeLe: string | null
  reference: string | null
  fournisseur: string | null
  telephone: string | null
}

type LigneLot = Record<string, unknown> & { merchant?: { name: string | null } | null }

const n = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0)

/** Ligne brute de `merchant_payouts` → lot typé. Les colonnes de 0065 absentes (base pas encore migrée) valent 0. */
export function lotDepuisLigne(l: LigneLot): LotReversement {
  return {
    id: String(l.id),
    marchandId: String(l.merchant_id),
    marchandNom: l.merchant?.name ?? 'Marchand',
    jour: String(l.period_end ?? '').slice(0, 10),
    mode: (['historique', 'quotidien', 'par_commande'].includes(String(l.mode)) ? String(l.mode) : 'historique') as ModeLot,
    nbCommandes: n(l.order_count),
    ventes: n(l.gross_articles),
    commission: n(l.commission_amount),
    retenues: n(l.adjustments),
    reportEntrant: n(l.carry_in),
    net: n(l.net_payable),
    reportSortant: n(l.carry_out),
    fraisVirement: n(l.frais_virement),
    fraisMarchand: n(l.frais_marchand),
    statut: String(l.status ?? 'pending'),
    payeLe: (l.paid_at as string | null) ?? null,
    reference: (l.payout_reference as string | null) ?? null,
    fournisseur: (l.payout_provider as string | null) ?? null,
    telephone: (l.payout_phone as string | null) ?? null,
  }
}

export const estVerse = (l: LotReversement) => l.statut === 'paid'

/** À virer d'abord, puis le plus récent d'abord. */
export function trierLots(lots: LotReversement[]): LotReversement[] {
  return [...lots].sort((a, b) => {
    if (estVerse(a) !== estVerse(b)) return estVerse(a) ? 1 : -1
    if (a.jour !== b.jour) return a.jour < b.jour ? 1 : -1
    return a.marchandNom.localeCompare(b.marchandNom, 'fr')
  })
}

export interface ResumeEnAttente {
  /** Total à virer maintenant. */
  aVirer: number
  /** Nombre de virements à faire (lots dont le net est > 0). */
  nbVirements: number
  /** Lots sans rien à virer (retenue ou report absorbé : ils se clôturent sans virement). */
  nbSansVirement: number
  commission: number
  /** Total des retenues déduites (positif). */
  retenues: number
  /** Frais de virement à NOTRE charge sur les virements à faire. */
  fraisANotreCharge: number
  /** Frais retenus aux marchands (0 tant que la décision « inclus dans notre commission » tient). */
  fraisRetenus: number
}

export function resumeEnAttente(lots: LotReversement[]): ResumeEnAttente {
  const r: ResumeEnAttente = { aVirer: 0, nbVirements: 0, nbSansVirement: 0, commission: 0, retenues: 0, fraisANotreCharge: 0, fraisRetenus: 0 }
  for (const l of lots.filter((x) => !estVerse(x))) {
    r.aVirer += l.net
    r.commission += l.commission
    r.retenues += Math.max(0, -l.retenues)
    if (l.net > 0) {
      r.nbVirements++
      r.fraisRetenus += l.fraisMarchand
      r.fraisANotreCharge += l.fraisVirement - l.fraisMarchand
    } else {
      r.nbSansVirement++
    }
  }
  return r
}

/** Corps de « Marquer tout comme versé » : les montants AFFICHÉS, que le serveur recompare. */
export function lotsAMarquer(lots: LotReversement[]): Array<{ id: string; montant: number }> {
  return lots.filter((l) => !estVerse(l)).map((l) => ({ id: l.id, montant: l.net }))
}

/** « Frais de virement (à notre charge) : 150 F » — seulement s'il y en a ; jamais déduits du marchand par défaut. */
export function libelleFrais(l: Pick<LotReversement, 'fraisVirement' | 'fraisMarchand'>): string | null {
  if (l.fraisVirement <= 0) return null
  if (l.fraisMarchand <= 0) return `Frais de virement (à notre charge) : ${l.fraisVirement.toLocaleString('fr-FR')} F`
  return `Frais de virement : ${l.fraisMarchand.toLocaleString('fr-FR')} F retenus au marchand sur ${l.fraisVirement.toLocaleString('fr-FR')} F`
}

export const NOM_FOURNISSEUR: Record<string, string> = { orange_money: 'Orange Money', wave: 'Wave' }

export interface SoldeMarchand {
  marchandId: string
  nom: string
  aVerser: number
  /** Ce que le marchand nous doit (retenues non couvertes par ses ventes). */
  dette: number
  /** Retenues pas encore absorbées dans un lot (signé : négatif = retenue). */
  ajustementsEnAttente: number
  plafond: number
  /** Dette au-delà du plafond : plus d'inclusion automatique, examen manuel. */
  aRevoir: boolean
  compteFournisseur: string | null
  compteTelephone: string | null
  compteVerifie: boolean
}

export function soldeDepuisLigne(l: Record<string, unknown>): SoldeMarchand {
  return {
    marchandId: String(l.merchant_id),
    nom: String(l.nom ?? 'Marchand'),
    aVerser: n(l.a_verser),
    dette: n(l.dette),
    ajustementsEnAttente: n(l.ajustements_en_attente),
    plafond: n(l.plafond),
    aRevoir: l.a_revoir === true,
    compteFournisseur: (l.compte_fournisseur as string | null) ?? null,
    compteTelephone: (l.compte_telephone as string | null) ?? null,
    compteVerifie: l.compte_verifie === true,
  }
}

/** Les erreurs du serveur (noms d'exception de la migration 0065) en français. */
const MESSAGES: Array<[RegExp, string]> = [
  [/montant_modifie/, 'Un montant a changé depuis l’affichage (une commande vient d’être ajoutée ou un collègue a agi). Rien n’a été marqué : l’écran est actualisé, vérifiez puis recommencez.'],
  [/reference_requise/, 'Saisissez la référence du virement (le numéro de transaction Orange Money ou Wave).'],
  [/reversement_deja_verse/, 'Ce reversement a déjà été marqué versé (un collègue vient de le faire).'],
  [/reversement_introuvable/, 'Reversement introuvable. Actualisez l’écran.'],
  [/forbidden|42501|permission denied/i, 'Vous n’avez pas le droit de gérer la paie.'],
  [/forcer_exige_un_marchand/, 'Passer outre le plafond se décide marchand par marchand.'],
  [/montant_requis/, 'Le montant ne peut pas être nul (négatif : retenue, positif : crédit).'],
  [/motif_requis/, 'Le motif est obligatoire : il apparaît sur le relevé du marchand.'],
  [/commande_etrangere/, 'Cette commande n’appartient pas à ce marchand.'],
  [/fuseau_invalide/, 'Fuseau horaire inconnu (exemple : Africa/Bamako).'],
]

export function messageErreur(e: unknown, repli = 'Opération impossible.'): string {
  const brut = e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e ?? '')
  const connu = MESSAGES.find(([re]) => re.test(brut))
  return connu ? connu[1] : brut || repli
}

// ── Serveur ───────────────────────────────────────────────────────────

/** Prépare les lots dus maintenant. Idempotent : rejouer ne change rien. */
export async function preparerReversements(): Promise<void> {
  const { error } = await supabase.rpc('generer_reversements_marchands')
  if (error) throw error
}

export async function listerLots(): Promise<LotReversement[]> {
  const { data, error } = await supabase
    .from('merchant_payouts')
    .select('*, merchant:merchants!merchant_id(name)')
    .order('period_end', { ascending: false })
    .limit(500)
  if (error) throw error
  return ((data ?? []) as unknown as LigneLot[]).map(lotDepuisLigne)
}

export async function listerSoldes(): Promise<SoldeMarchand[]> {
  const { data, error } = await supabase.rpc('soldes_marchands')
  if (error) throw error
  return ((data ?? []) as Array<Record<string, unknown>>).map(soldeDepuisLigne)
}

export interface LigneReleve {
  genre: 'commande' | 'ajustement'
  reference: string
  livreeLe: string | null
  ventes: number | null
  commission: number | null
  montant: number
  motif: string | null
}

export async function releveDuLot(id: string): Promise<LigneReleve[]> {
  const { data, error } = await supabase.rpc('releve_reversement', { p_payout: id })
  if (error) throw error
  return ((data ?? []) as Array<Record<string, unknown>>).map((l) => ({
    genre: l.genre === 'ajustement' ? 'ajustement' : 'commande',
    reference: String(l.reference ?? ''),
    livreeLe: (l.livree_le as string | null) ?? null,
    ventes: l.ventes === null || l.ventes === undefined ? null : n(l.ventes),
    commission: l.commission === null || l.commission === undefined ? null : n(l.commission),
    montant: n(l.montant),
    motif: (l.motif as string | null) ?? null,
  }))
}

export async function marquerLotVerse(
  lot: LotReversement,
  reference: string,
  fournisseur: string | null,
  telephone: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('marquer_reversement_verse', {
    p_payout: lot.id,
    p_montant: lot.net,
    p_reference: reference.trim() || null,
    p_fournisseur: fournisseur,
    p_telephone: telephone?.trim() || null,
  })
  if (error) throw error
}

/** « Marquer tout comme versé » : renvoie le nombre de lots marqués. */
export async function marquerToutVerse(lots: LotReversement[], reference: string): Promise<{ marques: number; dejaVerses: number }> {
  const { data, error } = await supabase.rpc('marquer_reversements_verses', {
    p_lots: lotsAMarquer(lots),
    p_reference: reference.trim() || null,
  })
  if (error) throw error
  const d = (data ?? {}) as { marques?: number; deja_verses?: number }
  return { marques: d.marques ?? 0, dejaVerses: d.deja_verses ?? 0 }
}

export async function enregistrerAjustement(
  marchandId: string,
  type: 'retenue' | 'credit',
  montantPositif: number,
  motif: string,
): Promise<void> {
  const { error } = await supabase.rpc('enregistrer_ajustement_marchand', {
    p_marchand: marchandId,
    p_montant: type === 'retenue' ? -Math.abs(Math.round(montantPositif)) : Math.abs(Math.round(montantPositif)),
    p_motif: motif.trim(),
    p_commande: null,
    p_genre: type === 'credit' ? 'correction' : 'litige',
  })
  if (error) throw error
}

/** Après examen : inclure ce marchand malgré son solde négatif au-delà du plafond. */
export async function forcerInclusion(marchandId: string): Promise<void> {
  const { error } = await supabase.rpc('generer_reversements_marchands', { p_marchand: marchandId, p_forcer: true })
  if (error) throw error
}

export async function fixerPlafondMarchand(marchandId: string, plafond: number | null): Promise<void> {
  const { error } = await supabase
    .from('merchant_reversement_reglages')
    .upsert({ merchant_id: marchandId, plafond_solde_negatif: plafond }, { onConflict: 'merchant_id' })
  if (error) throw error
}

// ── Réglages de la plateforme (platform_config, 0065) ─────────────────

export interface ReglagesReversement {
  payoutMode: 'quotidien' | 'par_commande'
  heureLimite: string
  fuseau: string
  delaiConfirmationMin: number
  plafondNegatif: number
  debut: string
  fraisVirement: number
  fraisACharge: 'plateforme' | 'marchand' | 'partage'
}

export const REGLAGES_PAR_DEFAUT: ReglagesReversement = {
  payoutMode: 'quotidien',
  heureLimite: '23:59',
  fuseau: 'Africa/Bamako',
  delaiConfirmationMin: 10,
  plafondNegatif: 50000,
  debut: '',
  fraisVirement: 0,
  fraisACharge: 'plateforme',
}

export function reglagesDepuisLigne(c: Record<string, unknown>): ReglagesReversement {
  const mode = c.payout_mode === 'par_commande' ? 'par_commande' : 'quotidien'
  const charge = c.reversement_frais_virement_a_charge
  return {
    payoutMode: mode,
    heureLimite: String(c.reversement_heure_limite ?? REGLAGES_PAR_DEFAUT.heureLimite).slice(0, 5),
    fuseau: String(c.reversement_fuseau ?? REGLAGES_PAR_DEFAUT.fuseau),
    delaiConfirmationMin: n(c.reversement_delai_confirmation_min ?? REGLAGES_PAR_DEFAUT.delaiConfirmationMin),
    plafondNegatif: n(c.reversement_plafond_negatif ?? REGLAGES_PAR_DEFAUT.plafondNegatif),
    debut: String(c.reversement_debut ?? '').slice(0, 10),
    fraisVirement: n(c.reversement_frais_virement),
    fraisACharge: charge === 'marchand' || charge === 'partage' ? charge : 'plateforme',
  }
}

/** Erreurs de saisie, une phrase par champ — vide = valide. */
export function validerReglages(r: ReglagesReversement): string[] {
  const e: string[] = []
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(r.heureLimite)) e.push('L’heure limite doit être au format HH:MM (exemple : 23:59).')
  if (!r.fuseau.trim()) e.push('Le fuseau horaire est obligatoire (exemple : Africa/Bamako).')
  if (!Number.isInteger(r.delaiConfirmationMin) || r.delaiConfirmationMin < 0 || r.delaiConfirmationMin > 1440)
    e.push('Le délai de confirmation automatique va de 0 à 1 440 minutes.')
  if (!Number.isInteger(r.plafondNegatif) || r.plafondNegatif < 0) e.push('Le plafond de solde négatif doit être un entier positif ou nul.')
  if (!Number.isInteger(r.fraisVirement) || r.fraisVirement < 0) e.push('Les frais de virement doivent être un entier positif ou nul (0 tant que les tarifs ne sont pas connus).')
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.debut)) e.push('La première journée gérée doit être une date (AAAA-MM-JJ).')
  return e
}

export async function lireReglages(): Promise<ReglagesReversement | null> {
  const { data, error } = await supabase.from('platform_config').select('*').eq('id', 1).single()
  if (error) throw error
  const c = data as Record<string, unknown>
  // Colonnes absentes = migration 0065 pas encore poussée.
  if (!('payout_mode' in c)) return null
  return reglagesDepuisLigne(c)
}

export async function ecrireReglages(r: ReglagesReversement): Promise<void> {
  const erreurs = validerReglages(r)
  if (erreurs.length > 0) throw new Error(erreurs[0])
  const { error } = await supabase
    .from('platform_config')
    .update({
      payout_mode: r.payoutMode,
      reversement_heure_limite: r.heureLimite,
      reversement_fuseau: r.fuseau.trim(),
      reversement_delai_confirmation_min: r.delaiConfirmationMin,
      reversement_plafond_negatif: r.plafondNegatif,
      reversement_debut: r.debut,
      reversement_frais_virement: r.fraisVirement,
      reversement_frais_virement_a_charge: r.fraisACharge,
    })
    .eq('id', 1)
  if (error) throw error
}
